import { ServiceInbox } from '../../services/service-inbox';
import { ServiceProjects } from '../../services/service-projects';
import { ServiceDownload } from '../../services/service-download';
import appState from '../../state/AppStateContainer';
import { FileScope } from '../../util/appEnum';
import { ConversationMessage, InboxAdapter, InboxItem, toInboxQuery } from './types';

const threadKeys = (item: InboxItem) => ({
  projectId: item.projectId,
  supplierId: item.supplierId,
  supplierMemberId: item.supplierMemberId,
  areaId: item.areaId,
});

export const purchaserInboxAdapter: InboxAdapter<InboxItem> = {
  // The inbox never surfaces quotationMessage as a latest message.
  messageTypes: ['standardMessage', 'bidMessage', 'intentionMessage'],
  createActivity: () => true,

  // Supplier+project group identity — keep in sync with the backend's groupKey (inboxService.js).
  groupKey: item => `${item.supplierId}__${item.projectId}`,
  counterpart: item => item.supplierName,

  fetchPage: opts => ServiceInbox.getInbox(toInboxQuery(opts)),
  fetchGroupPage: (lastItem, opts) => ServiceInbox.getInbox({
    ...toInboxQuery(opts),
    supplierId: lastItem.supplierId,
    projectIds: [lastItem.projectId],
  }),

  // No messageId → the whole thread. Mark-unread re-flags the latest supplier
  // message, which is what the unread count actually counts.
  markRead: item => ServiceProjects.markMessageRead(threadKeys(item)),
  markUnread: item => ServiceProjects.markMessageUnread(threadKeys(item)),
  refreshBadge: () => appState.refreshProjectNotifications(),
  markAllRead: () => ServiceProjects.clearAllNotifications(),

  loadThread: async item => {
    const data: { conversation?: ConversationMessage[] } = await ServiceProjects.getConversation(
      item.supplierId, item.projectId, item.supplierMemberId, item.areaId,
    );
    // The API returns newest-first.
    return [...(data?.conversation ?? [])].reverse();
  },
  sendReply: (item, text, files) => ServiceProjects.postAdminMessage(
    item.projectId, item.supplierId, item.supplierMemberId, text, files, item.areaId,
  ),
  isOwnMessage: msg => !!msg.adminId,
  downloadAttachment: (item, attachment) => ServiceDownload.downloadFile(
    attachment.file,
    attachment.fileName?.split('.').pop() || '',
    attachment.fileName,
    FileScope.ProjectConversation,
    { projectId: item.projectId, supplierId: item.supplierId, areaId: item.areaId, clientId: item.supplierMemberId },
  ),

  conversationLink: item =>
    `${appState._getCurrentOrgUrl()}/projects/${item.projectSlug}/${item.areaSlug}/${item.supplierSlug}/${item.supplierMemberSlug}`,
};
